import classnames from 'classnames';
import { RawDraftContentState } from 'draft-js';
import { isNull, isUndefined, noop } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { getWorldGeometryJSON } from '../../home/store';
import { IconButton } from '../../shared/button/IconButton';
import { Icon } from '../../shared/icon/Icon';
import { CaretDown, CaretSide, Delete, QuestionMark, SmileyNegative, SmileyNeutral, SmileyPositive, Tick, WarningSign } from '../../shared/icons';
import { PageLoadingDots } from '../../shared/loading-dots/PageLoadingDots';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { OverflowTooltip } from '../../shared/tooltip';
import styles from './NettingEngine.module.scss';
import { NettingEngineAnswerJustificationModal } from './NettingEngineAnswerJustificationModal';
import { NettingEngineConditionModal } from './NettingEngineConditionModal';
import { NettingEngineEnquireOverlay } from './NettingEngineEnquireOverlay';
import { NettingEngineMapOutline } from './NettingEngineMapOutline';
import { JurisdictionalAnswers, JurisdictionalNettingInformation, ProductTypeCovered, ProductTypeCoveredEnum, getSelectedProductCovered, setSelectedProductCovered, JurisdictionalNettingDetailsAnswer, setSelectedJurisdictionDetails, getAnswerJustificationModalOpen, setAnswerJustificationModalOpen, getSelectedJurisdictionDetails } from './store';
import { getCountryGeometry } from './store/utils';
import { NettingEngineSummary } from '../store/types';

const { green, red, grey, amber, fixedPrimary, yellowGreen } = styles;

// We are rolling back changes to transaction coverage for now until the data is caught up for all opinions
const productsCoveredDataUpToDate = false;

interface NettingEngineJurisdictionResultsProps {
    jurisdictionalResults: JurisdictionalNettingInformation;
    viewCustomConclusions: boolean;
    height: number;
    width: number;
    isLoading: boolean;
    index: number;
    isInstanceView: boolean;
    isSingleJurisdiction: boolean;
}

const PADDING = 20;
const MARGIN = 20;

export const NettingEngineJurisdictionResults: React.FC<NettingEngineJurisdictionResultsProps> = ({ jurisdictionalResults, viewCustomConclusions, height, width, isLoading, index, isInstanceView, isSingleJurisdiction }) => {
    const [transactionConditionModalOpen, setTransactionConditionModalOpen] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const selectedProductCovered = useAppSelector(getSelectedProductCovered);
    const worldGeometry = useAppSelector(getWorldGeometryJSON);
    const answerJustificationModalOpen = useAppSelector(getAnswerJustificationModalOpen);
    const selectedSmileyIconDetails = useAppSelector(getSelectedJurisdictionDetails);

    const {
        details,
        permittedToView,
        jurisdiction,
        counterpartyOffices,
        summary,
        clientSignOffDetails,
        clientSignOffSummary
    } = jurisdictionalResults;

    const country = getCountryGeometry(worldGeometry, jurisdiction);

    const customConclusionsExist = useMemo(() => !isNull(clientSignOffDetails) && viewCustomConclusions, [clientSignOffDetails, viewCustomConclusions]);

    const summaryValue = useMemo(() => customConclusionsExist ? clientSignOffSummary : summary, [customConclusionsExist, clientSignOffSummary, summary]);

    const fillColour = useMemo(() => {
        if (!permittedToView) return grey;
        if (summaryValue === NettingEngineSummary.YES) return green;
        if (summaryValue === NettingEngineSummary.YES_PRODUCTS_UNKNOWN) return yellowGreen;
        if (summaryValue === NettingEngineSummary.NO) return red;
        if (summaryValue === NettingEngineSummary.MAYBE) return amber;
        return grey;
    }, [permittedToView, summaryValue]);

    const svgHeight = useMemo(() => height - 30, [height]);
    const svgWidth = useMemo(() => width * 0.45, [width]);

    const leftHandComponent = useMemo(() => !country ? <div className={styles.jurisdictionWrapper}>{jurisdiction}</div> : <NettingEngineMapOutline country={country} fillColour={fillColour} svgHeight={svgHeight} svgWidth={svgWidth} jurisdiction={jurisdiction} />, [jurisdiction, country, fillColour, svgHeight, svgWidth]);

    const handleSmileyIconClick = useCallback((question: string, value: string, details: RawDraftContentState | null, icon: string) => {
        dispatch(setSelectedJurisdictionDetails({ question, value, details, icon }));
        dispatch(setAnswerJustificationModalOpen(true));
    }, [dispatch]);

    const onCloseAnswerJustificationModal = useCallback(() => dispatch(setAnswerJustificationModalOpen(false)), [dispatch]);

    const getIcon = useCallback((question: string, answerString: JurisdictionalNettingDetailsAnswer, noPositive = false, isLocalLaw = false, aetApplicable: string | undefined = undefined) => {
        const { value, details } = answerString;
        if (
            (!permittedToView && !isLocalLaw) ||
            (isLocalLaw && value === 'Not permitted to view')
        ) return <IconButton icon={SmileyNeutral} iconFill={grey} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'neutral')} />;
        if (question === 'Is automatic early termination required?') {
            if (value === 'Yes' && aetApplicable === 'Yes' || value === 'No') return <IconButton icon={SmileyPositive} iconFill={green} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'positive')} />;
            if (value === 'Yes' && !isUndefined(aetApplicable) && ['No', 'Not Applicable'].includes(aetApplicable)) return <IconButton icon={SmileyNegative} iconFill={red} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'negative')} />;
            return <IconButton icon={SmileyNeutral} iconFill={amber} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'neutral')} />;
        }
        if (!noPositive) {
            if (value === 'Yes' || value === 'Not relevant to head office') return <IconButton icon={SmileyPositive} iconFill={green} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'positive')} />;
            if (value === 'No') return <IconButton icon={SmileyNegative} iconFill={red} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'negative')} />;
            return <IconButton icon={SmileyNeutral} iconFill={amber} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'neutral')} />;
        } else {
            if (value === 'No' || value === 'Not relevant to head office') return <IconButton icon={SmileyPositive} iconFill={green} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'positive')} />;
            if (value === 'Yes') return <IconButton icon={SmileyNegative} iconFill={red} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'negative')} />;
            return <IconButton icon={SmileyNeutral} iconFill={amber} color={fixedPrimary} onClick={() => handleSmileyIconClick(question, value, details, 'neutral')} />;
        }
    }, [permittedToView, handleSmileyIconClick]);

    const summaryIconToShow = useMemo(() => {
        switch (summaryValue) {
            case NettingEngineSummary.YES:
                return { icon: Tick, color: green };
            case NettingEngineSummary.YES_PRODUCTS_UNKNOWN:
                return { icon: Tick, color: yellowGreen };
            case NettingEngineSummary.NO:
                return { icon: Delete, color: red };
            default:
                return { icon: WarningSign, color: amber };
        }
    }, [summaryValue]);

    const summaryOfJurisdiction = useMemo(() => <><span className={styles.answerText}>{summaryValue}</span><Icon icon={summaryIconToShow.icon} color={summaryIconToShow.color} /></>, [summaryIconToShow, summaryValue]);

    const getAnswerJSX = useCallback((question: string, answer: JurisdictionalNettingDetailsAnswer, noPositive = false, isLocalLaw = false, aetApplicable: string | undefined = undefined): JSX.Element => (
        <>
            <span className={styles.answerText}>{answer.value}</span>{getIcon(question, answer, noPositive, isLocalLaw, aetApplicable)}
        </>
    ), [getIcon]);

    const [productsSectionOpen, setProductsSectionOpen] = useState<boolean>(true);
    const sectionOpenIcon = productsSectionOpen ? CaretDown : CaretSide;

    const { agreementCovered, counterpartyCovered, governingLawRecognised, closeOutNettingEnforceable, automaticEarlyTerminationRequired, onShoreInsolvency, offShoreInsolvency, productsCovered, localLawIsValid, automaticEarlyTerminationApplicable } = customConclusionsExist ? clientSignOffDetails! : details;

    const handleIconClick = useCallback((label: string) => {
        const selectedProductType = productsCovered.find(({ product }) => product === label);
        if (!isUndefined(selectedProductType)) {
            dispatch(setSelectedProductCovered(selectedProductType));
        }
        setTransactionConditionModalOpen(true);
    }, [productsCovered, dispatch]);

    const getIconColor = useCallback((covered: ProductTypeCoveredEnum, conditions?: string) => {
        if (productsCoveredDataUpToDate) {
            if ([ProductTypeCoveredEnum.NO, ProductTypeCoveredEnum.UNCLEAR].includes(covered)) {
                return amber;
            }
            if (conditions === 'Yes') {
                return green;
            }
            if (conditions === 'No') {
                return red;
            }
            return amber;
        }
        switch (covered) {
            case ProductTypeCoveredEnum.YES:
                return green;
            case ProductTypeCoveredEnum.NO:
                return red;
            case ProductTypeCoveredEnum.UNCLEAR:
            default:
                return amber;
        }
    }, []);

    const getIconForProduct = useCallback((covered: ProductTypeCoveredEnum, conditions?: string) => {
        if (productsCoveredDataUpToDate) {
            if ([ProductTypeCoveredEnum.NO, ProductTypeCoveredEnum.UNCLEAR].includes(covered) || isUndefined(conditions)) {
                return QuestionMark;
            }
            if (covered === ProductTypeCoveredEnum.YES) {
                if (['Yes', 'Yes - with exceptions'].includes(conditions)) {
                    return Tick;
                }
                if (['No', 'No - with exceptions'].includes(conditions)) {
                    return Delete;
                }
                if (['Unclear', 'Not applicable'].includes(conditions)) {
                    return QuestionMark;
                }
            }
            return QuestionMark;
        }
        switch (covered) {
            case ProductTypeCoveredEnum.YES:
                return Tick;
            case ProductTypeCoveredEnum.NO:
                return Delete;
            case ProductTypeCoveredEnum.UNCLEAR:
            default:
                return QuestionMark;
        }
    }, []);

    const getProductsCoveredColumn = useCallback((column: ProductTypeCovered[]) => (
        <div className={styles.productColumn}>
            {column.map(({ product, covered, conditions }, index) => <div className={styles.productsCovered} key={index}>
                <OverflowTooltip className={styles.product} overlayText={product} />
                <div className={styles.covered}>
                    <IconButton
                        icon={getIconForProduct(covered, conditions)}
                        color={getIconColor(covered, conditions)}
                        onClick={() => handleIconClick(product)}
                    />
                </div>
            </div>)}
        </div>
    ), [handleIconClick, getIconColor, getIconForProduct]);

    const getProductsCovered = useCallback((products: ProductTypeCovered[]) => {
        if (!productsSectionOpen) {
            return null;
        }
        const isOdd = (num: number) => num % 2;
        const columnOne = products.filter((product, index) => !isOdd(index) ? product : null);
        const columnTwo = products.filter((product, index) => isOdd(index) ? product : null);

        return <div className={styles.productsCoveredWrapper}>
            <Scrollable>
                <div className={styles.productColumns}>
                    {getProductsCoveredColumn(columnOne)}
                    {getProductsCoveredColumn(columnTwo)}
                </div>
            </Scrollable>
        </div>;
    }, [productsSectionOpen, getProductsCoveredColumn]);

    const createQuestionAnswer = useCallback((question: string, answer: JurisdictionalNettingDetailsAnswer, noPositive = false, isLocalLaw = false, aetApplicable: string | undefined = undefined) => ({ question, answer: getAnswerJSX(question, answer, noPositive, isLocalLaw, aetApplicable) }), [getAnswerJSX]);

    const eachAnswer: JurisdictionalAnswers[] = useMemo(() => {
        const answers: JurisdictionalAnswers[] = [
            createQuestionAnswer('Is the selected Agreement covered by the opinion?', agreementCovered),
            createQuestionAnswer('Is the selected counterparty type covered by the opinion?', counterpartyCovered),
            createQuestionAnswer('Will the governing law of the contract be recognised?', governingLawRecognised),
            createQuestionAnswer('Is close out netting enforceable?', closeOutNettingEnforceable),
            createQuestionAnswer('Is automatic early termination required?', automaticEarlyTerminationRequired, true, false, automaticEarlyTerminationApplicable?.value),
            createQuestionAnswer('Could separate insolvency proceedings be instituted against a local branch?', onShoreInsolvency, true),
            createQuestionAnswer('Would multi-branch close-out netting be respected in a separate local branch insolvency?', offShoreInsolvency)
        ];
        if (!isNull(localLawIsValid)) {
            answers.push(createQuestionAnswer('Is the ISDA Master Agreement (when governed by the laws of your jurisdiction) enforceable?', localLawIsValid, false, true));
        }
        return answers;
    }, [agreementCovered, counterpartyCovered, governingLawRecognised, localLawIsValid, createQuestionAnswer, automaticEarlyTerminationRequired, closeOutNettingEnforceable, offShoreInsolvency, onShoreInsolvency, automaticEarlyTerminationApplicable]);

    const summaryAndProductsCoveredAnswers: JurisdictionalAnswers[] = useMemo(() => {
        const productsCoveredAnswer = productsCovered.length > 0 ? getProductsCovered(productsCovered) : getAnswerJSX('', { value: 'Information not in Ark', details: null });
        return [
            { question: 'Summary of the Jurisdiction:', answer: summaryOfJurisdiction },
            { question: 'Products Covered (click icons for details)', answer: productsCoveredAnswer, showIcon: true }
        ];
    }, [summaryOfJurisdiction, getProductsCovered, productsCovered, getAnswerJSX]);

    const left = (width + 20) * index + 'px';

    const noClientSignOffDetailsMessage = useMemo(() => isNull(clientSignOffDetails) && viewCustomConclusions ? 'Because there are no sign off details around this jurisdiction, you are still viewing system data' : null, [clientSignOffDetails, viewCustomConclusions]);

    const wrapperWidth = useMemo(() => `${width - (isSingleJurisdiction ? (PADDING + MARGIN) : 0)}px`, [width, isSingleJurisdiction]);

    useEffect(() => () => {
        dispatch(setAnswerJustificationModalOpen(false));
    }, [dispatch]);

    if (isLoading) {
        return (
            <div className={styles.nettingJurisdictionResults} style={{ height, width }}>
                <PageLoadingDots />
            </div>
        );
    }

    const offices = counterpartyOffices.map(office => office).join(', ');
    // This keeps the jurisdiction label centred above the country
    const marginLeft = `calc((45% / 2) - ${(jurisdiction.length * 10 / 2)}px)`;
    const counterpartyOfficesWidth = `calc(100% - calc((45% / 2) + ${(jurisdiction.length * 10 / 2)}px))`;

    return (
        <div className={styles.nettingJurisdictionResults} style={{ height: `${height}px`, width: wrapperWidth }}>
            <div className={styles.jurisdictionWrapper}>
                <div style={{ marginLeft: marginLeft, minWidth: 'fit-content' }}>{jurisdiction}</div>
                {counterpartyOffices.length > 0 && <div className={styles.counterpartyOfficesWrapper} style={{ maxWidth: counterpartyOfficesWidth }}>
                    <div className={styles.counterpartyOfficesLabel}> - Counterparty Offices:</div>
                    <OverflowTooltip overlayText={offices} className={styles.counterpartyOffices} />
                </div>}
            </div>
            <div className={styles.resultsWrapper}>
                <div className={styles.leftHandSummary}>
                    {leftHandComponent}
                </div>
                <div className={styles.noClientSignOffDetailsMessage}>
                    <div className={styles.message}>{noClientSignOffDetailsMessage}</div>
                    <div className={styles.rightHandSummary}>
                        <Scrollable>
                            <div className={styles.questionAndAnswerWrapper}>
                                {eachAnswer.map(({ question, answer }, index) => {
                                    return (
                                        <div className={styles.questionAndAnswer} key={index}>
                                            <div className={styles.question}>{question}</div>
                                            <div className={styles.answer}>{answer}</div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className={styles.summaryAndProductsCoveredAnswers}>
                                {summaryAndProductsCoveredAnswers.map(({ question, answer, showIcon }, index) => {
                                    const onClick = (productsSectionOpen: boolean) => showIcon ? setProductsSectionOpen(!productsSectionOpen) : noop;
                                    const questionIcon = showIcon && <div className={styles.questionIcon}><Icon icon={sectionOpenIcon} fontSize={14} /></div>;
                                    return (
                                        <div className={classnames(styles.questionAndAnswer, [styles.jurisdictionAndProductsCoveredWidth])} key={index}>
                                            <div className={styles.question} onClick={() => onClick(productsSectionOpen)}>{question}{questionIcon}</div>
                                            <div className={styles.answer}>{answer}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Scrollable>
                    </div>
                </div>
            </div>
            {!permittedToView && <NettingEngineEnquireOverlay jurisdiction={jurisdiction} width={width} left={left} />}
            {!isNull(selectedProductCovered) && <NettingEngineConditionModal isOpen={transactionConditionModalOpen} closeModal={() => setTransactionConditionModalOpen(false)} productCovered={selectedProductCovered} />}
            <NettingEngineAnswerJustificationModal isOpen={answerJustificationModalOpen} answerDetails={selectedSmileyIconDetails} closeModal={onCloseAnswerJustificationModal} width={svgWidth} height={svgHeight} isInstanceView={isInstanceView} />
        </div>
    );
};
